import { createRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "@/hooks/useSafeTranslation";
import { toast } from "@/lib/safe-toast";
import { MessageSquare, Trash2, LifeBuoy } from "lucide-react";
import { authenticatedRoute } from "../_authenticated";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Select,
} from "@/components/ui";
import { useAuth } from "@/contexts/useAuth";
import { toDisplayString, toSafeReactNode } from "@/lib/utils";
import { apiPost, apiDeleteWithBody } from "@/api";
import { signOut } from "@/auth";

const SUPPORT_MAIL = "support@takelead.app";

function SupportPage() {
  const { t } = useTranslation("common");
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;

  const [category, setCategory] = useState("feature");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);

  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const FEEDBACK_CATEGORIES = [
    { value: "feature", label: toDisplayString(t("support.categoryFeature")) },
    { value: "bug", label: toDisplayString(t("support.categoryBug")) },
    { value: "ux", label: toDisplayString(t("support.categoryUx")) },
    { value: "pricing", label: toDisplayString(t("support.categoryPricing")) },
  ];

  const submitFeedback = async () => {
    if (!token || !content.trim()) return;
    setSending(true);
    try {
      const res = await apiPost("/feedback", token, {
        content: content.trim(),
        category,
        source: "in_app",
      });
      if (res.ok) {
        toast.success(t("support.thankYou"));
        setContent("");
      } else {
        toast.error(t("support.sendError"));
      }
    } finally {
      setSending(false);
    }
  };

  const deleteAccount = async () => {
    if (!token) return;
    setDeleting(true);
    try {
      const res = await apiDeleteWithBody("/me", token, { confirm: true });
      if (res.ok) {
        toast.success(t("support.deleteSuccess"));
        await signOut();
      } else {
        toast.error(t("support.deleteError"));
      }
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-text">{toSafeReactNode(t("support.title"))}</h1>
        <p className="text-sm text-text-muted">{toSafeReactNode(t("support.subtitle"))}</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <LifeBuoy className="h-5 w-5 text-accent" aria-hidden />
            <CardTitle>{toSafeReactNode(t("support.contactTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("support.contactDescription"))}</CardDescription>
        </CardHeader>
        <CardContent>
          <a
            href={`mailto:${SUPPORT_MAIL}`}
            className="text-accent hover:underline font-medium"
          >
            {SUPPORT_MAIL}
          </a>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-accent" aria-hidden />
            <CardTitle>{toSafeReactNode(t("support.feedbackTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("support.feedbackDescription"))}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {FEEDBACK_CATEGORIES.map((c) => (
              <option key={toDisplayString(c.value)} value={toDisplayString(c.value)}>
                {toSafeReactNode(c.label)}
              </option>
            ))}
          </Select>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            placeholder={toDisplayString(t("support.feedbackPlaceholder"))}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text placeholder:text-text-muted resize-y"
          />
          <Button
            variant="primary"
            disabled={!content.trim() || sending}
            onClick={submitFeedback}
          >
            {sending ? toSafeReactNode(t("support.sending")) : toSafeReactNode(t("support.send"))}
          </Button>
        </CardContent>
      </Card>

      <Card className="border-danger/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-danger" aria-hidden />
            <CardTitle className="text-danger">{toSafeReactNode(t("support.deleteAccountTitle"))}</CardTitle>
          </div>
          <CardDescription>{toSafeReactNode(t("support.deleteAccountDescription"))}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!confirmDelete ? (
            <Button
              variant="danger"
              onClick={() => setConfirmDelete(true)}
            >
              {toSafeReactNode(t("support.deleteButton"))}
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="rounded-lg border border-danger/30 bg-danger/10 p-3 text-sm text-danger">
                {toSafeReactNode(t("support.deleteWarning"))}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  disabled={deleting}
                  onClick={deleteAccount}
                >
                  {deleting ? toSafeReactNode(t("support.deleting")) : toSafeReactNode(t("support.deleteConfirm"))}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setConfirmDelete(false)}
                >
                  {toSafeReactNode(t("support.cancel"))}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export const supportRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "support",
  component: SupportPage,
});
